import { apiClient } from './client';

// ─── Dashboard Report ──────────────────────────────────────────────────────────
export interface DashboardSummary {
  totalTransactions: number;
  cashInCount: number;
  cashInVolume: number;
  cashOutCount: number;
  cashOutVolume: number;
  totalCommissions: number;
  netVolume: number;
  avgTransactionSize: number;
}

export interface DashboardFloat {
  openingBalance: number;
  closingBalance: number;
  netChange: number;
  currentBalance: number;
  minBalance: number;
  alertThreshold: number;
  status: 'OK' | 'LOW' | 'CRITICAL';
}

export interface HourlyActivity {
  hour: number;
  cashIn: number;
  cashOut: number;
  count: number;
}

export interface TopCustomer {
  name: string;
  phone: string;
  volume: number;
  count: number;
}

export interface RecentTransaction {
  id: string;
  type: 'CASH_IN' | 'CASH_OUT';
  amount: number;
  commission: number;
  status: string;
  customerName: string;
  reference: string;
  createdAt: string;
  completedAt?: string;
}

export interface DashboardReport {
  date: string;
  generatedAt: string;
  summary: DashboardSummary;
  float: DashboardFloat;
  hourlyActivity: HourlyActivity[];
  topCustomers: TopCustomer[];
  recentTransactions: RecentTransaction[];
}

// ─── Daily / Weekly / Monthly Report ──────────────────────────────────────────
export interface ReportSummary {
  totalTransactions: number;
  cashInVolume: number;
  cashInCount: number;
  cashOutVolume: number;
  cashOutCount: number;
  totalCommissions: number;
  netVolume?: number;
  avgTransactionSize?: number;
}

export interface DailyBreakdown {
  date: string;
  transactions: number;
  commissions: number;
  volume: number;
  cashIn?: number;
  cashOut?: number;
}

export interface ReportFloat {
  openingBalance: number;
  closingBalance: number;
  netChange: number;
  currentBalance?: number;
  status?: string;
}

export interface Report {
  date?: string;
  summary: ReportSummary;
  float?: ReportFloat;
  dailyBreakdown?: DailyBreakdown[];
  hourlyActivity?: HourlyActivity[];
  topCustomers?: TopCustomer[];
  recentTransactions?: RecentTransaction[];
  openingFloat?: number;
  closingFloat?: number;
}

export interface AgentStatistics {
  totalTransactions: number;
  totalVolume: number;
  totalEarnings: number;
  averageTransactionSize: number;
}

// ─── Performance Dashboard ────────────────────────────────────────────────────
export interface PerformanceMonthly {
  transactionCount: number;
  commissions: number;
  cashInVolume: number;
  cashOutVolume: number;
  totalVolume: number;
  avgTransactionSize: number;
  goal: number;
  progress: number;
}

export interface PerformanceBestDay {
  date: string;
  commissions: number;
  count: number;
}

export interface PerformanceAllTime {
  transactionCount: number;
  volume: number;
}

export interface PerformanceData {
  commissionRate: number;
  avgTransactionSize: number;
  commissionChange?: number;
  monthly: PerformanceMonthly;
  bestDay?: PerformanceBestDay;
  allTime?: PerformanceAllTime;
}

export interface PerformancePeriod {
  month: number;
  year: number;
  monthName: string;
}

export interface WeeklyChartItem {
  weekStart: string;
  label: string;
  cashIn: number;
  cashOut: number;
  commissions: number;
  count: number;
}

export interface DailyBreakdownItem {
  date: string;
  cashIn: number;
  cashOut: number;
  commissions: number;
  count: number;
}

export interface PerformanceDashboard {
  period: PerformancePeriod;
  agent: { agentCode: string; businessName: string; commissionRate: number };
  performance: PerformanceData;
  dailyBreakdown: DailyBreakdownItem[];
  weeklyChart: WeeklyChartItem[];
}

// ─── API ───────────────────────────────────────────────────────────────────────
export const reportsApi = {
  getDashboardReport: async (date: string): Promise<DashboardReport> => {
    const { data } = await apiClient.get('/agent/me/reports/dashboard', {
      params: { date },
    });
    // Handle response wrapped in data/report/dashboard key
    const raw = data?.data ?? data?.report ?? data?.dashboard ?? data;
    // Normalize summary — handle alternate field names
    const s = raw?.summary ?? {};
    const summary: DashboardSummary = {
      totalTransactions: s.totalTransactions ?? s.total_transactions ?? s.transactions ?? 0,
      cashInCount: s.cashInCount ?? s.cash_in_count ?? s.depositCount ?? 0,
      cashInVolume: s.cashInVolume ?? s.cash_in_volume ?? s.depositVolume ?? s.totalCashIn ?? 0,
      cashOutCount: s.cashOutCount ?? s.cash_out_count ?? s.withdrawalCount ?? 0,
      cashOutVolume: s.cashOutVolume ?? s.cash_out_volume ?? s.withdrawalVolume ?? s.totalCashOut ?? 0,
      totalCommissions: s.totalCommissions ?? s.commissions ?? s.total_commissions ?? 0,
      netVolume: s.netVolume ?? s.net_volume ?? 0,
      avgTransactionSize: s.avgTransactionSize ?? s.avg_transaction_size ?? s.averageAmount ?? 0,
    };
    // Normalize float
    const f = raw?.float ?? raw?.floatData ?? raw?.agentFloat ?? {};
    const float: DashboardFloat = {
      openingBalance: f.openingBalance ?? f.opening_balance ?? f.openingFloat ?? 0,
      closingBalance: f.closingBalance ?? f.closing_balance ?? f.closingFloat ?? 0,
      netChange: f.netChange ?? f.net_change ?? f.difference ?? 0,
      currentBalance: f.currentBalance ?? f.current_balance ?? f.balance ?? 0,
      minBalance: f.minBalance ?? f.min_balance ?? 0,
      alertThreshold: f.alertThreshold ?? f.alert_threshold ?? 0,
      status: f.status ?? 'OK',
    };
    return {
      date: raw?.date ?? date,
      generatedAt: raw?.generatedAt ?? raw?.generated_at ?? new Date().toISOString(),
      summary,
      float,
      hourlyActivity: raw?.hourlyActivity ?? raw?.hourly_activity ?? raw?.hourly ?? [],
      topCustomers: raw?.topCustomers ?? raw?.top_customers ?? raw?.customers ?? [],
      recentTransactions: raw?.recentTransactions ?? raw?.recent_transactions ?? raw?.transactions ?? [],
    };
  },

  getDailyReport: async (date?: string): Promise<Report> => {
    const reportDate = date || new Date().toISOString().split('T')[0];
    const { data } = await apiClient.get(`/agent/me/reports/daily/${reportDate}`);
    return data;
  },

  getWeeklyReport: async (weekStart: string): Promise<Report> => {
    const { data } = await apiClient.get('/agent/me/reports/weekly', {
      params: { weekStart },
    });
    return data;
  },

  getMonthlyReport: async (month: number, year: number): Promise<Report> => {
    const { data } = await apiClient.get('/agent/me/reports/monthly', {
      params: { month, year },
    });
    return data;
  },

  generateReport: async (date: string): Promise<{ message: string; report: DashboardReport }> => {
    const { data } = await apiClient.post('/agent/me/reports/generate', { date });
    return data;
  },

  exportPDF: async (params: { period: string; date: string }): Promise<Blob> => {
    const { data } = await apiClient.get('/agents/me/reports/export/pdf', {
      params,
      responseType: 'blob',
    });
    return data;
  },

  getStatistics: async (days: number = 30): Promise<AgentStatistics> => {
    const { data } = await apiClient.get('/agent/statistics', {
      params: { days },
    });
    return data;
  },

  getPerformanceDashboard: async (month?: number, year?: number): Promise<PerformanceDashboard> => {
    const params: Record<string, number> = {};
    if (month !== undefined) params.month = month;
    if (year !== undefined) params.year = year;
    const { data } = await apiClient.get('/agent/me/dashboard/performance', { params });
    const raw = data?.data ?? data;
    // Normalize performance
    const p = raw?.performance ?? {};
    const m = p?.monthly ?? {};
    const performance: PerformanceData = {
      commissionRate: p.commissionRate ?? p.commission_rate ?? 0,
      avgTransactionSize: p.avgTransactionSize ?? p.avg_transaction_size ?? m.avgTransactionSize ?? 0,
      commissionChange: p.commissionChange ?? p.commission_change,
      monthly: {
        transactionCount: m.transactionCount ?? m.transaction_count ?? 0,
        commissions: m.commissions ?? 0,
        cashInVolume: m.cashInVolume ?? m.cash_in_volume ?? 0,
        cashOutVolume: m.cashOutVolume ?? m.cash_out_volume ?? 0,
        totalVolume: m.totalVolume ?? m.total_volume ?? 0,
        avgTransactionSize: m.avgTransactionSize ?? m.avg_transaction_size ?? 0,
        goal: m.goal ?? 100,
        progress: m.progress ?? 0,
      },
      bestDay: p.bestDay ?? p.best_day,
      allTime: p.allTime ?? p.all_time,
    };
    return {
      period: raw?.period ?? { month: month ?? new Date().getMonth() + 1, year: year ?? new Date().getFullYear(), monthName: '' },
      agent: raw?.agent ?? { agentCode: '', businessName: '', commissionRate: 0 },
      performance,
      dailyBreakdown: raw?.dailyBreakdown ?? raw?.daily_breakdown ?? [],
      weeklyChart: raw?.weeklyChart ?? raw?.weekly_chart ?? [],
    };
  },
};
